import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ButtonHoverAnimation from "./button/ButtonHoverAnimation";
import { useAccountInfo } from "../utils/constants";
import {
  BrainBalance,
  BrainCreditsBalance,
  useBrainCreditsBalance,
  useTokenBalance,
} from "./balances";
import {
  useAccount,
  useWriteContract,
  useReadContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { formatUnits, parseAbi, parseEther, decodeEventLog } from "viem";
import { brain721Abi } from "../utils/brain721Abi";
import RedeemSuccess from "./redeemsuccess";
import { useSpring, animated } from "react-spring";

const brainCA = process.env.REACT_APP_BRAIN_ERC721_CA;
const brainCreditsCA = process.env.REACT_APP_BRAIN_CREDITS;

const erc20ABI = parseAbi([
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
]);

const Redeem = () => {
  const [credits, setCredits] = useState(0);
  const [mintedTokenIds, setMintedTokenIds] = useState([]);
  const [redeemType, setRedeemType] = useState(null);
  const [prevCredits, setPrevCredits] = useState(0);
  const { shortenedAddress } = useAccountInfo();
  const navigate = useNavigate();
  const account = useAccount();
  const brainCreditsBalance = useBrainCreditsBalance();

  // Add near other hooks
  const { number } = useSpring({
    number: credits,
    from: { number: prevCredits },
    reset: false,
  });

  const {
    data: approvalHash,
    error: approvalError,
    isPending: isApprovalPending,
    writeContract: writeApprovalContract,
  } = useWriteContract();

  const {
    data: redeemHash,
    error: redeemError,
    isPending: isRedeemPending,
    writeContract: writeRedeemContract,
  } = useWriteContract();

  const {
    data: partialHash,
    error: partialError,
    isPending: partialPending,
    writeContract: writePartialContract,
  } = useWriteContract();

  const { isLoading: isApprovalConfirming, isSuccess: isApprovalConfirmed } =
    useWaitForTransactionReceipt({
      hash: approvalHash,
    });

  const {
    isLoading: isRedeemConfirming,
    isSuccess: isRedeemConfirmed,
    data: redeemReceipt,
  } = useWaitForTransactionReceipt({
    hash: redeemHash,
  });

  const {
    isLoading: isContributionConfirming,
    isSuccess: isContributionSuccess,
  } = useWaitForTransactionReceipt({
    hash: partialHash,
  });

  const { data: allowance } = useReadContract({
    address: brainCreditsCA,
    abi: erc20ABI,
    functionName: "allowance",
    args: [account.address, brainCA],
    query: {
      refetchInterval: 3000,
    },
  });

  useEffect(() => {
    if (redeemReceipt) {
      const tokenIds = redeemReceipt.logs
        .filter((log) => {
          try {
            const event = decodeEventLog({
              abi: brain721Abi,
              data: log.data,
              topics: log.topics,
            });
            return (
              event.eventName === "Transfer" &&
              log.address.toLowerCase() === brainCA.toLowerCase()
            );
          } catch {
            return false;
          }
        })
        .map((log) => {
          const event = decodeEventLog({
            abi: brain721Abi,
            data: log.data,
            topics: log.topics,
          });
          return Number(event.args.tokenId);
        });

      setMintedTokenIds(tokenIds);
    }
  }, [redeemReceipt]);

  const formattedAllowance = allowance ? Number(formatUnits(allowance, 18)) : 0;
  const isAllowed = formattedAllowance >= Number(credits);

  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    const parsedValue = inputValue === "" ? "" : parseFloat(inputValue);
    setCredits(parsedValue);
  };

  const getRedemptionType = (credits) => {
    const creditsAmount = parseFloat(credits);

    if (creditsAmount === 0) {
      return null;
    } else if (creditsAmount < 1000) {
      return (
        <>
          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
            <small className="font-mono muted text-uppercase">
              * You are redeeming less than 1000 Brain Credits. Your brain
              credits will be pooled with others, and you will receive partial
              ownership of a brain relative to your share of the 1000 total
              credits.
            </small>
          </div>
        </>
      );
    } else if (creditsAmount % 1000 === 0) {
      return (
        <>
          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
            <small className="font-mono muted text-uppercase">
              You are redeeming
            </small>
            <small className="font-mono text-uppercase d-block">
              <animated.span>{number.to((n) => Math.floor(n))}</animated.span>{" "}
              CREDITS
            </small>
          </div>
          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
            <small className="font-mono muted text-uppercase">
              You will receive
            </small>
            <small className="font-mono text-uppercase d-block">
              {credits / 1000}x Brain NFT
            </small>
          </div>
          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
            <small className="font-mono muted text-uppercase d-block">
              Brain Exchange Rate
            </small>
            <small className="font-mono text-uppercase d-block">
              1k BRAIN CREDITS = 1 NFT brain
            </small>
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
            <small className="font-mono muted text-uppercase">
              * You will receive a mixture of whole Brains, and partial Brain
              ownership. This will require two separate transactions to be
              signed. If you'd only like whole Brains then redeem in increments
              of 1000.
            </small>
          </div>
        </>
      );
    }
  };

  const redemptionType = getRedemptionType(credits);

  const isFractionalRedeem =
    credits % 1000 !== 0 && credits > 0 && credits < 1000;

  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => setIsVisible(!isVisible);
  const isMobile = window.innerWidth <= 575.98;

  const navigateToInventory = () => navigate("/my-inventory");

  async function approveCredits() {
    writeApprovalContract({
      address: brainCreditsCA,
      abi: erc20ABI,
      functionName: "approve",
      args: [brainCA, parseEther(credits.toString())],
    });
  }

  const handleRedemptionTypeChange = (type) => {
    setCredits(0);
    setRedeemType(type);
  };

  const handleFullRedemptionChange = (direction) => {
    const change = direction === "increase" ? 1000 : -1000;
    const newValue = Math.max(0, credits + change);
    // Ensure we don't exceed balance
    setCredits(Math.min(newValue, brainCreditsBalance));
  };

  async function redeemCredits(e) {
    e.preventDefault();

    if (!isAllowed) {
      await approveCredits();
      return;
    }

    const creditsAmount = parseFloat(credits);

    if (redeemType === "partial") {
      return writePartialContract({
        address: brainCA,
        abi: brain721Abi,
        functionName: "contributeBrainCredits",
        args: [parseEther(creditsAmount.toString())],
      });
    }

    return writeRedeemContract({
      address: brainCA,
      abi: brain721Abi,
      functionName: "redeemBrain",
      args: [parseEther(creditsAmount.toString())],
    });
  }

  const buttonDisabled =
    Number(credits) > brainCreditsBalance ||
    isApprovalPending ||
    isRedeemPending ||
    isApprovalConfirming ||
    isRedeemConfirming ||
    isContributionConfirming ||
    partialPending ||
    (redeemType === "partial" && Number(credits) >= 1000);

  const getButtonText = () => {
    if (isApprovalPending || isApprovalConfirming) return "Approving...";
    if (
      isRedeemPending ||
      isRedeemConfirming ||
      isContributionConfirming ||
      partialPending
    )
      return "Redeeming...";
    if (Number(credits) > brainCreditsBalance) return "GET MORE BRAIN CREDITS";
    if (redeemType === "partial" && Number(credits) >= 1000)
      return "USE FULL REDEMPTION";
    if (!isAllowed) return "Approve";
    if (isFractionalRedeem) return "PARTIAL REDEMPTION";
    return "REDEEM";
  };

  return (
    <>
      <ButtonHoverAnimation />
      <main data-barba="container" data-barba-namespace="secondary">
        <div className="min-svh-100 d-flex align-items-center justify-content-center px-3 px-sm-4 px-md-5  opacity-load">
          <div className="container w-100">
            <div className="spacer"></div>
            <div className="my-4">
              <ul className="nav-secondary">
                <li className="h1">
                  <Link to="/burning">Burn</Link>
                </li>
                <li className="h1 current">
                  <Link to="/burning-redeem">Redeem</Link>
                </li>
              </ul>

              <div className="mt-4 mt-lg-5">
                <div className="box mx-auto">
                  <div className="box-head py-3 py-md-4 px-3 px-md-5 d-flex justify-content-between align-items-end">
                    <div>
                      <small className="font-mono text-uppercase d-block mb-2 d-flex align-items-center">
                        <span className="muted">Brain Credits Available</span>{" "}
                      </small>
                      <p className="fs-3 mb-0 lh-1 text-uppercase">
                        <BrainCreditsBalance />
                      </p>
                    </div>
                    <button
                      className="text-end unstyled-button"
                      onClick={navigateToInventory}
                    >
                      <em>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="21"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                          className="position-relative"
                          style={{ top: "-2px" }}
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.0605 0H16.0605C17.7392 0 19.1763 1.03408 19.7697 2.5H6.35123C6.94462 1.03408 8.38178 0 10.0605 0ZM0.0604858 10V14H4.06049C5.16506 14 6.06049 13.1046 6.06049 12C6.06049 10.8954 5.16506 10 4.06049 10H0.0604858ZM20.0605 4H4.06049C1.85135 4 0.0604858 5.79086 0.0604858 8V8.5H4.06049C5.99348 8.5 7.56049 10.067 7.56049 12C7.56049 13.933 5.99348 15.5 4.06049 15.5H0.0604858V16C0.0604858 18.2091 1.85135 20 4.06049 20H16.0605C18.2696 20 20.0605 18.2091 20.0605 16V4Z"
                            fill="#75FBFD"
                          ></path>
                        </svg>
                      </em>
                      <span className="ps-1">{shortenedAddress}</span>
                      <div className="font-mono text-blue text-uppercase mb-1">
                        View Inventory
                      </div>
                    </button>
                  </div>
                  <hr className="my-0 border-green" />
                  <div className="pt-3 pt-md-4 px-3 px-md-5">
                    <form
                      action=""
                      className={
                        isRedeemConfirmed || isContributionSuccess
                          ? "submitted"
                          : ""
                      }
                    >
                      <div className="form">
                        {!redeemType ? (
                          // Initial redemption type selection
                          <>
                            <h4 className="mb-3 text-center font-mono text-uppercase">
                              Select Redemption Type
                            </h4>
                            <div className="row">
                              <div className="col-12 col-md-6 mb-3">
                                <Link
                                  className="btn-2 btnHover2 w-100"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleRedemptionTypeChange("partial");
                                  }}
                                  style={{ padding: "24px 12px" }}
                                >
                                  <span
                                    className="position-relative"
                                    style={{ fontSize: "1rem" }}
                                  >
                                    Partial Ownership
                                    <small className="d-block font-mono muted text-uppercase mt-2">
                                      Redeem less than 1000 Credits
                                    </small>
                                  </span>
                                </Link>
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <Link
                                  className="btn-2 btnHover2 w-100"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleRedemptionTypeChange("full");
                                  }}
                                  style={{ padding: "24px 12px" }}
                                >
                                  <span
                                    className="position-relative"
                                    style={{ fontSize: "1rem" }}
                                  >
                                    Full Ownership
                                    <small className="d-block font-mono muted text-uppercase mt-2">
                                      Redeem in increments of 1000 Credits
                                    </small>
                                  </span>
                                </Link>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Redemption type toggle */}
                            <div className="row mb-4">
                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div
                                    className={`cursor-pointer font-mono text-uppercase ${
                                      redeemType === "partial"
                                        ? "text-cyan"
                                        : "muted"
                                    }`}
                                    onClick={() =>
                                      handleRedemptionTypeChange("partial")
                                    }
                                  >
                                    Partial Ownership
                                  </div>
                                  <div
                                    className={`cursor-pointer font-mono text-uppercase ${
                                      redeemType === "full"
                                        ? "text-cyan"
                                        : "muted"
                                    }`}
                                    onClick={() =>
                                      handleRedemptionTypeChange("full")
                                    }
                                  >
                                    Full Ownership
                                  </div>
                                </div>
                              </div>
                            </div>

                            {redeemType === "partial" ? (
                              // Partial redemption input
                              <div className="row">
                                <div className="col-sm-12 mb-3">
                                  <label
                                    htmlFor="InputPepcoin"
                                    className="form-label d-block small opacity-75 font-mono ps-3"
                                  >
                                    Brain Credits to Pool
                                  </label>
                                  <div className="position-relative">
                                    <div className="input-icon p-3">
                                      <img src="img/icon-b.png" alt="" />
                                    </div>
                                    <input
                                      type="number"
                                      step="any"
                                      className="form-control ps-6"
                                      id="InputPepcoin"
                                      placeholder="BRAIN CREDITS"
                                      value={credits}
                                      onChange={handleInputChange}
                                    />
                                    {/* <div className="input-text small opacity-75 font-mono text-uppercase p-3 d-flex align-items-center">
                                      <button
                                        className="max-btn"
                                        type="button"
                                        onClick={() =>
                                          setCredits(brainCreditsBalance)
                                        }
                                      >
                                        max
                                      </button>
                                    </div> */}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // Full redemption input
                              <div className="row">
                                <div className="col-sm-8 mb-3">
                                  <label
                                    htmlFor="InputBrains"
                                    className="form-label d-block small opacity-75 font-mono ps-3"
                                  >
                                    # of Brains
                                  </label>
                                  <div className="position-relative">
                                    <div className="input-icon p-3">
                                      <img src="img/icon-brains.png" alt="" />
                                    </div>
                                    <input
                                      type="text"
                                      className="form-control ps-6"
                                      id="InputBrains"
                                      value={
                                        Math.floor(credits / 1000) + " Brain(s)"
                                      }
                                      placeholder="NFT Brains"
                                      disabled
                                      style={{ cursor: "default" }}
                                    />
                                    <div className="input-text small opacity-75 font-mono text-uppercase p-3 d-flex align-items-center">
                                      <button
                                        className="max-btn"
                                        type="button"
                                        onClick={() =>
                                          setCredits(
                                            brainCreditsBalance -
                                              (brainCreditsBalance % 1000)
                                          )
                                        }
                                      >
                                        max
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="font-mono text-uppercase d-flex align-items-center col-sm-4 gap-2 mb-3">
                                  <button
                                    className="qty-btn"
                                    type="button"
                                    onClick={() =>
                                      handleFullRedemptionChange("decrease")
                                    }
                                    disabled={credits < 1000}
                                  >
                                    -
                                  </button>
                                  <button
                                    type="button"
                                    className="qty-btn"
                                    onClick={() =>
                                      handleFullRedemptionChange("increase")
                                    }
                                    disabled={
                                      credits + 1000 > brainCreditsBalance
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              // disabled={buttonDisabled}
                              disabled={true}
                              onClick={redeemCredits}
                            >
                              {/* {getButtonText()} */}
                              REDEMPTIONS DISABLED
                            </button>
                            <div className="pt-4">{redemptionType}</div>
                          </>
                        )}
                        <div className="d-sm-flex justify-content-between mb-3 mb-sm-2">
                          <small className="font-mono muted text-uppercase">
                            * Brain redemptions are temporarily disabled
                          </small>
                        </div>
                      </div>
                      <RedeemSuccess
                        creditsRedeemed={credits}
                        brainsReceived={Math.floor(credits / 1000)}
                        hash={redeemHash}
                        redeemType={redeemType}
                        tokenIds={mintedTokenIds}
                        partialHash={partialHash}
                      />
                    </form>
                  </div>
                  <div className="pb-3 pb-md-4 px-3 px-md-5">
                    {isMobile && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: isVisible ? "auto" : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="readMoreContent">
                          <hr className="my-0 my-sm-4" />
                          <ul className="list-1">
                            <li>
                              Redeeming 1000 Brain Credits gives you 1 Brain
                              NFT.
                            </li>
                            <li>
                              Redeeming less than 1000 Brain Credits gives you
                              partial ownership of a brain.
                            </li>
                            <li>
                              Brains redeemed via Brain Credits are ERC-721
                              tokens. The Brain smart contract allows for the
                              creations of a Brain Token (ERC-20, different from
                              Brain Credits) for tokenizing that Brain.
                            </li>
                            <li>
                              Brain NFTs and Brain Tokens can be managed in the{" "}
                              <Link to="/my-inventory">
                                My Inventory section
                              </Link>
                              .
                            </li>
                          </ul>
                        </div>
                      </motion.div>
                    )}
                    {!isMobile && (
                      <div className="readMoreContent read-more-content-mobile">
                        <hr className="my-0 my-sm-4" />
                        <ul className="list-1">
                          <li>
                            Redeeming 1000 Brain Credits gives you 1 Brain NFT.
                          </li>
                          <li>
                            Redeeming less than 1000 Brain Credits gives you
                            partial ownership of a brain.
                          </li>
                          <li>
                            Brains redeemed via Brain Credits are ERC-721
                            tokens. The Brain smart contract allows for the
                            creations of a Brain Token (ERC-20, different from
                            Brain Credits) for tokenizing that Brain.
                          </li>
                          <li>
                            Brain NFTs and Brain Tokens can be managed in the{" "}
                            <Link to="/my-inventory">My Inventory section</Link>
                            .
                          </li>
                        </ul>
                      </div>
                    )}

                    <div
                      className={`readMore d-sm-none ${
                        isVisible ? "open" : ""
                      }`}
                      onClick={toggleVisibility}
                    >
                      <strong>
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12.0068"
                            cy="12.9766"
                            r="12"
                            transform="rotate(90 12.0068 12.9766)"
                            fill="#75FBFD"
                          />
                          <path
                            d="M15.249 11.8496L12.0073 14.1041L8.76562 11.8496"
                            stroke="black"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </strong>
                      <span className="ps-1">
                        {isVisible ? "Close" : "Read More"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <Link
                className="underline-on-hover-2 d-flex align-items-center mx-auto mt-4 justify-content-center"
                to="/acquire-brain"
              >
                <em>
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12.4526" cy="12.3848" r="12" fill="#75FBFD" />
                    <path
                      d="M13.7493 15.6265L11.1559 12.3848L13.7493 9.14306"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </em>
                <span className="ps-2">Go Back</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Redeem;
